import React, { useState } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Row,
  Col,
  notification,
  Space,
} from "antd";
import { PatientFormDTO } from "../models/PatientForm";
import { useLoaderProvider } from "../contexts/LoaderContext";
import { contactService } from "../services/contactService";

const { Title, Paragraph } = Typography;

const PatientPage: React.FC = () => {
  const { showLoader, hideLoader } = useLoaderProvider();
  const [form] = Form.useForm<PatientFormDTO>();
  const [captchaValue, setCaptchaValue] = useState<string>("");
  const [captchaInput, setCaptchaInput] = useState<string>("");

  // Genera un captcha semplice
  const generateCaptcha = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaValue(result);
    setCaptchaInput("");
  };

  // Genera il captcha al caricamento del componente
  React.useEffect(() => {
    generateCaptcha();
  }, []);

  const onFinish = async (values: PatientFormDTO) => {
    // Verifica il captcha
    if (captchaInput.toUpperCase() !== captchaValue) {
      notification.error({
        message: "Errore",
        description: "Il codice captcha inserito non è corretto. Riprova.",
      });
      generateCaptcha();
      return;
    }

    try {
      showLoader();

      await contactService.patientContact(values);

      form.resetFields();
      generateCaptcha();

      notification.success({
        message: "Messaggio inviato con successo",
        description:
          "Il tuo messaggio è stato inviato correttamente. Ti risponderemo al più presto.",
      });
    } catch (error) {
      notification.error({
        message: "Errore",
        description:
          "Si è verificato un errore durante l'invio del messaggio. Riprova più tardi.",
      });
    } finally {
      hideLoader();
    }
  };

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto", padding: "24px" }}>
      <Row gutter={[16, 16]} justify="center">
        <Col xs={24} md={16}>
          <Card>
            <Title level={2}>Contattaci</Title>
            <Paragraph>
              Se hai riconosciuto almeno due fra segni e sintomi riportati sul
              Portale, compila il form sottostante per contattarci. Ti
              risponderemo al più presto.
            </Paragraph>

            <Form
              name="patient-form"
              form={form}
              layout="vertical"
              onFinish={onFinish}
              autoComplete="off"
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="name"
                    label="Nome"
                    rules={[
                      {
                        required: true,
                        message: "Per favore inserisci il tuo nome!",
                      },
                      {
                        min: 2,
                        message: "Il nome deve essere di almeno 2 caratteri!",
                      },
                    ]}
                  >
                    <Input placeholder="Inserisci il tuo nome" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="surname"
                    label="Cognome"
                    rules={[
                      {
                        required: true,
                        message: "Per favore inserisci il tuo cognome!",
                      },
                      {
                        min: 2,
                        message:
                          "Il cognome deve essere di almeno 2 caratteri!",
                      },
                    ]}
                  >
                    <Input placeholder="Inserisci il tuo cognome" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="phoneNumber"
                    label="Numero di cellulare"
                    rules={[
                      {
                        pattern: /^[+]?[0-9\s\-()]{10,15}$/,
                        message: "Inserisci un numero di cellulare valido!",
                      },
                    ]}
                  >
                    <Input placeholder="Inserisci il tuo numero di cellulare" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      {
                        required: true,
                        message: "Per favore inserisci la tua email!",
                      },
                      {
                        type: "email",
                        message: "Inserisci una email valida!",
                      },
                    ]}
                  >
                    <Input placeholder="Inserisci la tua email" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="message"
                label="Messaggio"
                rules={[
                  {
                    required: true,
                    message: "Per favore inserisci il tuo messaggio!",
                  },
                  {
                    min: 10,
                    message: "Il messaggio deve essere di almeno 10 caratteri!",
                  },
                ]}
              >
                <Input.TextArea
                  rows={6}
                  placeholder="Descrivi i sintomi che hai riconosciuto e qualsiasi altra informazione rilevante..."
                />
              </Form.Item>

              <Form.Item
                label="Verifica di sicurezza"
                required
                help="Inserisci il codice mostrato nell'immagine"
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        background: "#f0f0f0",
                        padding: "8px 16px",
                        borderRadius: "4px",
                        fontFamily: "monospace",
                        fontSize: "18px",
                        fontWeight: "bold",
                        letterSpacing: "2px",
                        color: "#333",
                        border: "2px solid #d9d9d9",
                        minWidth: "120px",
                        textAlign: "center",
                      }}
                    >
                      {captchaValue}
                    </div>
                    <Button
                      type="link"
                      onClick={generateCaptcha}
                      style={{ padding: "4px 8px" }}
                    >
                      Aggiorna
                    </Button>
                  </div>
                  <Input
                    placeholder="Inserisci il codice captcha"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    style={{ maxWidth: "200px" }}
                  />
                </Space>
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" size="large" block>
                  Invia Messaggio
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PatientPage;
